import React, { useState, useEffect } from "react";
import Sidebar from "./sidebar";
import WeeklyOccupancyTable from "./WeeklyOccupancy";

const BASE_URL = "https://cuhkmigration-dot-optimus-hk.df.r.appspot.com";

const HistoricalReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [reportType, setReportType] = useState("daily");
  const [selectedArea, setSelectedArea] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [show2ADesks, setShow2ADesks] = useState(false);
  const [desksOccupancyData, setDesksOccupancyData] = useState([]);

  const rowsPerPage = 40;

  useEffect(() => {
    // Set default date range to current month
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    setStartDate(formatDate(firstDay));
    setEndDate(formatDate(lastDay));
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchData();
    }
  }, [reportType, selectedArea, startDate, endDate]);

  //  synchronize dates for daily reports
  useEffect(() => {
    if (reportType === "daily" && selectedArea !== "2A desks" && startDate) {
      setEndDate(startDate);
    }
  }, [reportType, selectedArea, startDate]);

  const formatDate = (date) => {
    return date.toISOString().split("T")[0];
  };

  const getLastDayOfMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleAreaChange = (e) => {
    const value = e.target.value;
    setSelectedArea(value);
    setShow2ADesks(value === "2A desks");
  };

  const fetchData = async () => {
    if (!startDate || !endDate) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let hellaData = [];
      let sensemaxData = [];

      // Determine which endpoints to call based on report type
      if (reportType === "hourly") {
        // For hourly, update to use date range
        const hellaResponse = await fetch(
          `${BASE_URL}/hella-hourly-occupancy?from=${startDate}&to=${endDate}`
        );
        hellaData = await hellaResponse.json();

        // Only fetch Sensemax data if "all" or "1B" is selected
        if (selectedArea === "all" || selectedArea === "1B") {
          const sensemaxResponse = await fetch(
            `${BASE_URL}/sensemax-hourly-occupancy?from=${startDate}&to=${endDate}`
          );
          sensemaxData = await sensemaxResponse.json();
        }
      } else if (reportType === "daily") {
        // Format date with time components for daily reports
        const startDateTime =
          reportType === "daily" && selectedArea !== "2A desks"
            ? `${startDate}T00:00:00.000000Z`
            : startDate;

        const endDateTime =
          reportType === "daily" && selectedArea !== "2A desks"
            ? `${startDate}T23:59:00.000000Z`
            : endDate || startDate;

        // Always fetch Hella data
        const hellaResponse = await fetch(
          `${BASE_URL}/hella-daily-occupancy?fromDate=${startDateTime}&toDate=${endDateTime}`
        );
        hellaData = await hellaResponse.json();

        // Only fetch Sensemax data if "all" or "1B" is selected
        if (selectedArea === "all" || selectedArea === "1B") {
          const sensemaxResponse = await fetch(
            `${BASE_URL}/sensemax-daily-occupancy?fromDate=${startDateTime}&toDate=${endDateTime}`
          );
          sensemaxData = await sensemaxResponse.json();
        }
      } else if (reportType === "weekly") {
        // For weekly, we'll use daily endpoints but process the data differently
        const hellaResponse = await fetch(
          `${BASE_URL}/hella-daily-occupancy?fromDate=${startDate}&toDate=${endDate}`
        );
        hellaData = await hellaResponse.json();

        if (selectedArea === "all" || selectedArea === "1B") {
          const sensemaxResponse = await fetch(
            `${BASE_URL}/sensemax-daily-occupancy?fromDate=${startDate}&toDate=${endDate}`
          );
          sensemaxData = await sensemaxResponse.json();
        }

        // Process data to aggregate by week
        hellaData = aggregateByWeek(hellaData);
        sensemaxData = aggregateByWeek(sensemaxData);
      } else if (reportType === "monthly") {
        // Format date parameters for monthly endpoints
        const fromTimestamp = new Date(startDate);
        const toTimestamp = new Date(endDate);

        // Add time to make it full day
        toTimestamp.setHours(23, 59, 59, 999);

        const fromISO = fromTimestamp.toISOString();
        const toISO = toTimestamp.toISOString();

        const hellaResponse = await fetch(
          `${BASE_URL}/hella-monthly-occupancy?fromTimestamp=${fromISO}&toTimestamp=${toISO}`
        );
        hellaData = await hellaResponse.json();

        if (selectedArea === "all" || selectedArea === "1B") {
          const sensemaxResponse = await fetch(
            `${BASE_URL}/sensemax-monthly-occupancy?fromTimestamp=${fromISO}&toTimestamp=${toISO}`
          );
          sensemaxData = await sensemaxResponse.json();
        }
      }

      if (selectedArea === "2A desks") {
        try {
          // Fetch desk occupancy data
          const desksResponse = await fetch(
            `https://njs-01.optimuslab.space/api/desks/occupancy/history?startDate=${startDate}&endDate=${endDate}`
          );
          const desksData = await desksResponse.json();

          // Fetch desk info for assignee names
          const deskInfoResponse = await fetch(
            "https://njs-01.optimuslab.space/api/desks"
          );
          const deskInfo = await deskInfoResponse.json();

          // Create a map of desk_id to desk info
          const deskInfoMap = {};
          deskInfo.forEach((desk) => {
            deskInfoMap[desk.desk_id] = desk;
          });

          // Merge desk info with occupancy data
          const mergedData = desksData.map((desk) => {
            const deskDetails = deskInfoMap[desk.desk_id] || {};
            return {
              ...desk,
              assignee_name: deskDetails.assignee_name || "N/A",
            };
          });

          setDesksOccupancyData(mergedData);
          setLoading(false);
          return; // Exit early since we're handling desks differently
        } catch (err) {
          console.error("Error fetching desks occupancy data:", err);
          setError(
            "Failed to fetch desks occupancy data. Please try again later."
          );
        }
      }

      // Filter data based on selected area
      if (selectedArea !== "all") {
        hellaData = hellaData.filter((item) => item.areaId === selectedArea);
        sensemaxData = sensemaxData.filter(
          (item) => item.areaId === selectedArea
        );
      }

      // Format data to match table requirements
      const formattedData = formatData(
        [...hellaData, ...sensemaxData],
        reportType
      );
      setReportData(formattedData);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to fetch data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Modify this function to calculate average based on total days in date range
  const calculateAvgOccupancy = (deskData) => {
    if (
      !deskData ||
      !deskData.daily_occupancy ||
      deskData.daily_occupancy.length === 0
    ) {
      return 0;
    }

    // Calculate total days in the selected date range
    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);
    const totalDays =
      Math.floor((endDateObj - startDateObj) / (24 * 60 * 60 * 1000)) + 1;

    // Sum all percentages
    const totalPercentage = deskData.daily_occupancy.reduce(
      (sum, day) => sum + parseFloat(day.percentage || 0),
      0
    );

    // Calculate the average by dividing by total days in range
    return totalPercentage / totalDays;
  };

  const aggregateByWeek = (data) => {
    // Group data by week
    const weeklyData = {};

    data.forEach((item) => {
      const date = new Date(item.year, item.month - 1, item.day);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay()); // Sunday as week start

      const weekKey = `${weekStart.getFullYear()}-${
        weekStart.getMonth() + 1
      }-${weekStart.getDate()}_${item.areaId}`;

      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = {
          year: weekStart.getFullYear(),
          month: weekStart.getMonth() + 1,
          day: weekStart.getDate(),
          areaId: item.areaId,
          total_in: 0,
          max_occupancy: 0,
          week_start: weekStart,
          week_end: new Date(weekStart),
        };
        weeklyData[weekKey].week_end.setDate(weekStart.getDate() + 6);
      }

      weeklyData[weekKey].total_in += item.total_in;
      weeklyData[weekKey].max_occupancy = Math.max(
        weeklyData[weekKey].max_occupancy,
        item.max_occupancy
      );
    });

    return Object.values(weeklyData);
  };

  const formatData = (data, type) => {
    return data.map((item, index) => {
      let formattedDate = "";
      let hour = "N/A";

      if (type === "hourly") {
        formattedDate = `${item.day}/${item.month}/${item.year}`;
        hour = `${item.hour}:00`;
      } else if (type === "daily") {
        formattedDate = `${item.day}/${item.month}/${item.year}`;
      } else if (type === "weekly") {
        const weekStart = new Date(item.week_start);
        const weekEnd = new Date(item.week_end);
        formattedDate = `${weekStart.getDate()}/${
          weekStart.getMonth() + 1
        }/${weekStart.getFullYear()} - ${weekEnd.getDate()}/${
          weekEnd.getMonth() + 1
        }/${weekEnd.getFullYear()}`;
      } else if (type === "monthly") {
        // Format month names
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        formattedDate = `${monthNames[item.month - 1]} ${item.year}`;
      }

      return {
        id: index + 1,
        date: formattedDate,
        hour: hour,
        visits: item.total_in,
        peak_occupancy: item.max_occupancy,
        area_id: item.areaId,
      };
    });
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;

    if (name === "startDate") {
      setStartDate(value);

      // For daily reports with non-2A desks, keep end date synced with start date
      if (reportType === "daily" && selectedArea !== "2A desks") {
        setEndDate(value);
      }
      // For other reports, ensure end date is after start date
      else if (endDate && new Date(value) > new Date(endDate)) {
        setEndDate(value);
      }
    } else if (name === "endDate") {
      // Handle special cases for months without 31 days
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(value);

      // If month end validation is needed
      if (reportType === "monthly") {
        const endMonth = endDateObj.getMonth() + 1;
        const endYear = endDateObj.getFullYear();
        const lastDayOfMonth = getLastDayOfMonth(endYear, endMonth);

        // Adjust date if it exceeds the last day of the month
        if (endDateObj.getDate() > lastDayOfMonth) {
          const adjustedDate = new Date(endYear, endMonth - 1, lastDayOfMonth);
          setEndDate(formatDate(adjustedDate));
          return;
        }
      }

      setEndDate(value);
    }
  };

  const handleReportTypeChange = (e) => {
    const newReportType = e.target.value;
    setReportType(newReportType);

    // When switching to daily report type for non-2A desks, set end date equal to start date
    if (newReportType === "daily" && selectedArea !== "2A desks" && startDate) {
      setEndDate(startDate);
    }

    // Reset current page when changing report type
    setCurrentPage(1);
  };

  const exportCSV = () => {
    if (reportData.length === 0) return;

    // Create CSV content
    const headers = [
      "Date",
      "Hour",
      "Total Visits",
      "Max Occupancy",
      "Area ID",
    ];
    const csvContent = [
      headers.join(","),
      ...reportData.map((row) =>
        [row.date, row.hour, row.visits, row.peak_occupancy, row.area_id].join(
          ","
        )
      ),
    ].join("\n");

    // Create download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `historical_report_${reportType}_${
        new Date().toISOString().split("T")[0]
      }.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Calculate pagination
  const filteredData = reportData;
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  // Calculate average peak occupancy
  const averagePeakOccupancy =
    reportData.length > 0
      ? Math.round(
          reportData.reduce((sum, item) => sum + item.peak_occupancy, 0) /
            reportData.length
        )
      : 0;

  return (
    <div className="bg-gray-50">
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      {/* Top Navigation Bar */}
      <div className="bg-white p-6 shadow flex items-center relative">
        {/* Hamburger Menu */}
        <button
          onClick={toggleSidebar}
          className="text-gray-600 hover:text-gray-900 focus:outline-none z-10"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-8 w-10"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>

        {/* Centered Logo */}
        <div className="absolute left-0 right-0 mx-auto flex justify-center">
          <img src="/download.png" alt="CUHK INNOPORT" className="h-12" />
        </div>
      </div>
      <div className="relative">
        {/* Blue Header */}
        <div className="bg-[#000976] text-white pt-6 px-4 sm:px-6 pb-20 rounded-b-xl w-full">
          <div className="flex flex-wrap sm:flex-nowrap justify-between items-center gap-4">
            <h2 className="2xl:text-2xl xl:text-2xl text-xl font-semibold">Generate Report</h2>
            <button
              className="bg-white text-[#000976] px-4 py-2 rounded-md font-medium border border-[#000976] whitespace-nowrap"
              onClick={exportCSV}
            >
              Export CSV
            </button>
          </div>
        </div>

        {/* White Filter Box */}
        <div className="relative -mt-16 bg-white p-4 sm:p-6 shadow-md rounded-xl w-[90%] max-w-5xl mx-auto z-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {/* Date Selector */}
            <div>
              <label className="block text-gray-800 font-medium text-md mb-1">
                Select Date Range
              </label>
              <div className="flex text-gray-500 gap-2 flex-wrap">
                <input
                  type="date"
                  name="startDate"
                  className="border-stone-300 border p-2 rounded-full w-full"
                  value={startDate}
                  onChange={handleDateChange}
                />
                {(reportType !== "daily" || selectedArea === "2A desks") && (
                  <input
                    type="date"
                    name="endDate"
                    className="border-stone-300 border p-2 rounded-full w-full"
                    value={endDate}
                    onChange={handleDateChange}
                  />
                )}
              </div>
            </div>

            {/* Area Selector */}
            <div>
              <label className="block text-gray-800 font-medium text-md mb-1">
                Select Area
              </label>
              <select
                className="border-stone-300 border text-gray-500 p-2 rounded-full w-full"
                value={selectedArea}
                onChange={handleAreaChange}
              >
                <option value="all">All Areas</option>
                <option value="UGA">UGA</option>
                <option value="UGB">UGB</option>
                <option value="1B">1B</option>
                <option value="2A">2A</option>
                <option value="2B">2B</option>
                <option value="2A desks">2A desks</option>
              </select>
            </div>

            {/* Report Type Selector */}
            <div>
              <label className="block text-gray-800 font-medium text-md mb-1">
                Report Type
              </label>
              <select
                className="border-stone-300 border text-gray-500 p-2 rounded-full w-full"
                value={reportType}
                onChange={handleReportTypeChange}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>
        </div>

       
      </div>

      <div className={`mt-6 pl-6 sm:pl-8 md:pl-10 lg:pl-12 xl:pl-12 2xl:pl-14`}>
        <h2 className="xl:text-xl lg:text-lg text-lg font-semibold text-gray-800 xl:mb-4 mb-0">
          {selectedArea === "2A desks"
            ? "2A Desks Report"
            : `${selectedArea === "all" ? "All Areas" : selectedArea} Report`}
        </h2>
      </div>

      {show2ADesks ? (
        <div className="mt-8 mb-10">
          {loading ? (
            <div className="flex justify-center items-center h-48">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#000976]"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">{error}</div>
          ) : (
            <div className="overflow-x-auto bg-white rounded-xl shadow-md border border-gray-300 w-[90%] mx-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-[#c8c8c855]">
                  <tr>
                    <th className="text-center px-4 py-4">Desk ID</th>
                    <th className="text-center px-4 py-4">Date</th>
                    <th className="text-center px-4 py-4">Name</th>
                    <th className="text-center px-4 py-4">
                      Desk Occupancy (%)
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {desksOccupancyData.length > 0 ? (
                    desksOccupancyData.map((item, index) => (
                      <tr
                        key={item.desk_id}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-6 py-4 text-center whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.desk_id}
                        </td>

                        <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-600">
                          {startDate} to {endDate}
                        </td>

                        <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-600">
                          {item.assignee_name || "N/A"}
                        </td>

                        <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-600">
                          {Math.round(calculateAvgOccupancy(item))}%
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="4"
                        className="px-6 py-10 text-center text-gray-500"
                      >
                        No data available for the selected period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8 mb-10 overflow-x-auto rounded-xl border border-[#d4d4d4] bg-white w-[95%] mx-auto">
          {/* Your existing table code */}
          {loading ? (
            <div className="flex justify-center items-center h-48">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#000976]"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">{error}</div>
          ) : reportData.length === 0 ? (
            <div className="text-center py-8 text-gray-600">
              No data available for the selected criteria. Please adjust your
              filters.
            </div>
          ) : (
            <table className="table-auto custom-shadow w-full">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-center px-4 py-4">Area ID</th>
                  <th className="text-center px-4 py-4">Date</th>
                  <th className="text-center px-4 py-4">Total Visits</th>
                  <th className="text-center px-4 py-4">Max Occupancy</th>
                </tr>
              </thead>
              <tbody>
                {currentRows.map((item, index) => (
                  <tr
                    key={item.id}
                    className={
                      index === currentRows.length - 1
                        ? ""
                        : "border-b border-gray-300"
                    }
                  >
                    <td className="text-center px-4 py-4">{item.area_id}</td>
                    <td className="text-center px-4 py-4">{item.date}</td>
                    <td className="text-center px-4 py-4">{item.visits}</td>
                    <td className="text-center px-4 py-4 text-gray-700">
                      {item.peak_occupancy}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {reportData.length > 0 && (
            <div className="flex items-center justify-between px-8 py-3 border-t border-gray-300 bg-white">
              <div className="text-[12px] sm:text-[12px] md:text-sm text-gray-600">
                Showing {indexOfFirstRow + 1}-
                {Math.min(indexOfLastRow, filteredData.length)} of{" "}
                {filteredData.length} rows
              </div>
              <div className="flex items-center space-x-2">
                {/* Previous button */}
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-md border bg-white text-gray-700 disabled:opacity-50"
                >
                  &lt;
                </button>

                {/* Scrollable pagination */}
                <div className="flex items-center space-x-2 overflow-x-auto max-w-md px-1 py-1">
                  {totalPages > 0 &&
                    Array.from({ length: totalPages }, (_, idx) => (
                      <button
                        key={idx + 1}
                        onClick={() => setCurrentPage(idx + 1)}
                        className={`min-w-8 w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-md border ${
                          currentPage === idx + 1
                            ? "bg-[#000976] text-white"
                            : "bg-white text-gray-700"
                        }`}
                      >
                        {idx + 1}
                      </button>
                    ))}
                </div>

                {/* Next button */}
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="w-8 h-8 flex items-center justify-center rounded-md border bg-white text-gray-700 disabled:opacity-50"
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HistoricalReport;